import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import Chip from "@material-ui/core/Chip";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  successMsg: { color: "#4caf50", fontSize: "13px" },
  stepBox: { display: "flex", alignItems: "center", padding: "10px 14px", borderRadius: "8px", marginBottom: "8px", border: "1px solid #e0e0e0" },
  stepIcon: { width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", flexShrink: 0 },
  stepName: { fontWeight: "bold", fontSize: "13px", flex: 1 },
  link: {
    color: "#3f51b5",
    cursor: "pointer",
    fontWeight: "600",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" }
  }
};

const STATUS_TONE = { Cleared: "success", Rejected: "danger", Pending: "warning" };

const DEPARTMENTS = [
  { key: "library", label: "Library" },
  { key: "fees", label: "Fees & Accounts" },
  { key: "hostel", label: "Hostel" },
  { key: "academic", label: "Academic Registrar" },
  { key: "dean", label: "Dean of Students" }
];

class ClearanceList extends Component {
  state = {
    clearances: [],
    loading: true,
    selected: null,
    detailOpen: false,
    initiateOpen: false,
    studentSearch: "",
    studentResults: [],
    searchLoading: false,
    selectedStudent: null,
    saving: false,
    error: null,
    success: null,
    remarkMap: {}
  };

  componentDidMount() { this.fetchClearances(); }

  fetchClearances = async () => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/clearances?limit=100");
      const d = res.data.data || res.data;
      this.setState({ clearances: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  searchStudents = async (q) => {
    if (!q || q.length < 2) { this.setState({ studentResults: [] }); return; }
    this.setState({ searchLoading: true });
    try {
      const res = await api.get("/students", { params: { search: q, limit: 10 } });
      const d = res.data.data || res.data;
      this.setState({ studentResults: Array.isArray(d) ? d : d.data || [], searchLoading: false });
    } catch (e) { this.setState({ searchLoading: false }); }
  };

  handleInitiate = async () => {
    const { selectedStudent } = this.state;
    if (!selectedStudent) { this.setState({ error: "Select a student." }); return; }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/clearances", { studentId: selectedStudent.id });
      this.setState({ initiateOpen: false, saving: false, success: "Clearance initiated." });
      this.fetchClearances();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed.", saving: false });
    }
  };

  handleApprove = async (clearanceId, department) => {
    const remark = this.state.remarkMap[department] || "";
    this.setState({ saving: true });
    try {
      await api.patch(`/clearances/${clearanceId}/approve`, { department, remark });
      this.setState({ saving: false });
      const res = await api.get(`/clearances/${clearanceId}`);
      this.setState({ selected: res.data.data || res.data });
      this.fetchClearances();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Approval failed.", saving: false });
    }
  };

  handleReject = async (clearanceId, department) => {
    const remark = this.state.remarkMap[department] || "";
    if (!remark) { this.setState({ error: "A remark is required when rejecting." }); return; }
    this.setState({ saving: true });
    try {
      await api.patch(`/clearances/${clearanceId}/reject`, { department, remark });
      this.setState({ saving: false });
      const res = await api.get(`/clearances/${clearanceId}`);
      this.setState({ selected: res.data.data || res.data });
      this.fetchClearances();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Rejection failed.", saving: false });
    }
  };

  openDetail = async (c) => {
    this.setState({ detailOpen: true, selected: c, remarkMap: {}, error: null });
    try {
      const res = await api.get(`/clearances/${c.id}`);
      this.setState({ selected: res.data.data || res.data });
    } catch (e) { /* use cached */ }
  };

  render() {
    const { classes, history } = this.props;
    const {
      clearances, loading, selected, detailOpen, initiateOpen,
      studentSearch, studentResults, searchLoading, selectedStudent,
      saving, error, success, remarkMap
    } = this.state;

    const overallStatus = (c) => {
      const steps = c.steps || [];
      if (steps.length > 0 && steps.every(s => s.status === "approved")) return "Cleared";
      if (steps.some(s => s.status === "rejected")) return "Rejected";
      return "Pending";
    };

    const columns = [
      {
        id: "stdNo",
        header: "Std No",
        meta: { variant: "mono" },
        cell: info => {
          const student = info.row.original.student || {};
          return student.id ? (
            <span className={classes.link} onClick={() => history.push(`/bwillis/students/${student.id}`)}>
              {student.studentNo || "—"}
            </span>
          ) : (student.studentNo || "—");
        }
      },
      {
        id: "student",
        header: "Student",
        accessorFn: c => {
          const student = c.student || {};
          return `${student.firstName || ""} ${student.lastName || ""}`.trim() || "—";
        }
      },
      { id: "programme", header: "Programme", accessorFn: c => c.student?.programme?.name || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const label = overallStatus(info.row.original);
          return <Pill tone={STATUS_TONE[label] || "muted"}>{label}</Pill>;
        }
      },
      {
        id: "progress",
        header: "Progress",
        accessorFn: c => {
          const approved = (c.steps || []).filter(s => s.status === "approved").length;
          const total = (c.steps || []).length || DEPARTMENTS.length;
          return `${approved}/${total} dept.`;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" simple onClick={() => this.openDetail(info.row.original)}>View</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          {success && <p className={classes.successMsg}>&#10003; {success}</p>}
          <DataTable
            title="Student Clearance"
            subtitle="Multi-department sign-off workflow"
            actions={(
              <Button size="sm" color="primary" onClick={() => this.setState({
                initiateOpen: true, error: null, selectedStudent: null, studentSearch: "", studentResults: []
              })}>
                Initiate Clearance
              </Button>
            )}
            columns={columns}
            data={clearances}
            isLoading={loading}
            emptyState="No clearance records found."
          />
        </GridItem>

        {/* Clearance Detail Dialog */}
        <Dialog open={detailOpen} onClose={() => this.setState({ detailOpen: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={`Clearance Detail${selected ? ` — ${selected.student?.firstName || ""} ${selected.student?.lastName || ""}` : ""}`} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            {selected && DEPARTMENTS.map(dept => {
              const step = (selected.steps || []).find(s => s.department === dept.key);
              const status = step?.status || "pending";
              const iconBg = status === "approved" ? "#e8f5e9" : status === "rejected" ? "#ffebee" : "#f5f5f5";
              const iconColor = status === "approved" ? "#2e7d32" : status === "rejected" ? "#b71c1c" : "#bbb";
              return (
                <div key={dept.key} className={classes.stepBox}>
                  <div className={classes.stepIcon} style={{ background: iconBg }}>
                    {status === "approved"
                      ? <CheckIcon style={{ color: iconColor, fontSize: "18px" }} />
                      : status === "rejected"
                        ? <CloseIcon style={{ color: iconColor, fontSize: "18px" }} />
                        : <span style={{ color: "#bbb", fontSize: "16px" }}>&#9675;</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className={classes.stepName}>{dept.label}</div>
                    {step?.remark && <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{step.remark}</div>}
                    {status === "pending" && (
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "6px" }}>
                        <input
                          type="text"
                          placeholder="Remark (required to reject)..."
                          value={remarkMap[dept.key] || ""}
                          onChange={e => this.setState({ remarkMap: { ...remarkMap, [dept.key]: e.target.value } })}
                          style={{ flex: 1, padding: "4px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "12px" }}
                        />
                        <Button size="sm" color="success" simple onClick={() => this.handleApprove(selected.id, dept.key)} disabled={saving}>
                          Approve
                        </Button>
                        <Button size="sm" color="danger" simple onClick={() => this.handleReject(selected.id, dept.key)} disabled={saving}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                  <Chip
                    label={status}
                    style={{
                      background: status === "approved" ? "#e8f5e9" : status === "rejected" ? "#ffebee" : "#f5f5f5",
                      color: status === "approved" ? "#2e7d32" : status === "rejected" ? "#b71c1c" : "#999",
                      fontSize: "11px", height: "22px"
                    }}
                  />
                </div>
              );
            })}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ detailOpen: false, error: null })}>Close</Button>
          </DialogFooter>
        </Dialog>

        {/* Initiate Clearance Dialog */}
        <Dialog open={initiateOpen} onClose={() => this.setState({ initiateOpen: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Initiate Student Clearance" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Search Student</label>
              <input
                type="text"
                placeholder="Type name or student number..."
                value={studentSearch}
                onChange={e => { this.setState({ studentSearch: e.target.value }); this.searchStudents(e.target.value); }}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", marginTop: "4px", boxSizing: "border-box" }}
              />
              {searchLoading && <CircularProgress size={16} style={{ marginTop: "4px" }} />}
              {studentResults.length > 0 && (
                <div style={{ border: "1px solid #ddd", borderRadius: "4px", maxHeight: "160px", overflowY: "auto", marginTop: "4px" }}>
                  {studentResults.map(s => (
                    <div key={s.id}
                      onClick={() => this.setState({ selectedStudent: s, studentSearch: `${s.studentNo} — ${s.firstName} ${s.lastName}`, studentResults: [] })}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#f5f5f5"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                    >
                      {s.studentNo} — {s.firstName} {s.lastName}
                    </div>
                  ))}
                </div>
              )}
              {selectedStudent && (
                <div style={{ marginTop: "12px", padding: "10px 14px", background: "#e8f5e9", borderRadius: "6px" }}>
                  <span style={{ fontWeight: "bold", color: "#2e7d32" }}>
                    Selected: {selectedStudent.studentNo} — {selectedStudent.firstName} {selectedStudent.lastName}
                  </span>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ initiateOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleInitiate} disabled={saving || !selectedStudent}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Initiate"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

ClearanceList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ClearanceList));
